import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Image,
  Alert,
  ScrollView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { usePersistentCart, PersistentCartItem } from '../context/PersistentCartContext';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '@react-navigation/native';
import APIService from '../services/api';

const CartScreenSimple: React.FC = () => {
  const { state, updateQuantity, removeFromCart, clearCart } = usePersistentCart();
  const { user } = useAuth();
  const navigation = useNavigation();
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [walletBalance, setWalletBalance] = useState<string>('0.00');
  const [loadingWallet, setLoadingWallet] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // Log para debug - se ejecuta en cada render
  useEffect(() => {
    console.log('🛒 CartScreenSimple renderizado');
    console.log('🛒 Items:', state.items.length);
    console.log('🛒 isLoading:', state.isLoading);
    console.log('🛒 clearCart función disponible:', typeof clearCart);
  }, [state.items.length, state.isLoading, clearCart]);

  // Cargar saldo de billetera virtual
  useEffect(() => {
    const loadWalletBalance = async () => {
      if (user) {
        try {
          setLoadingWallet(true);
          console.log('💰 CartScreenSimple: Cargando saldo de billetera virtual');
          const walletData = await APIService.getWalletBalance();
          setWalletBalance(walletData.saldo_billetera);
          console.log('💰 CartScreenSimple: Saldo cargado:', walletData.saldo_billetera);
        } catch (error) {
          console.error('❌ CartScreenSimple: Error al cargar billetera:', error);
          setWalletBalance('0.00');
        } finally {
          setLoadingWallet(false);
        }
      }
    };

    loadWalletBalance();
  }, [user]);

  const handleQuantityChange = async (productId: number, change: number) => {
    if (isUpdating) {
      console.log('⏸️ Ya hay una actualización en proceso, ignorando...');
      return;
    }
    
    console.log('🔢 handleQuantityChange called:', { productId, change });
    const currentItem = state.items.find(item => item.id_producto === productId);
    if (currentItem) {
      const currentQuantity = Number(currentItem.cantidad) || 1;
      const newQuantity = currentQuantity + change;
      console.log('🔢 Current quantity:', currentQuantity, 'New quantity:', newQuantity);
      if (newQuantity > 0) {
        try {
          setIsUpdating(true);
          await updateQuantity(productId, newQuantity);
        } catch (error) {
          console.error('Error updating quantity:', error);
          alert('Error: No se pudo actualizar la cantidad');
        } finally {
          setIsUpdating(false);
        }
      } else {
        try {
          setIsUpdating(true);
          await removeFromCart(productId);
        } catch (error) {
          console.error('Error removing item:', error);
          alert('Error: No se pudo eliminar el producto');
        } finally {
          setIsUpdating(false);
        }
      }
    }
  };

  const handleRemoveItem = async (productId: number, productName: string) => {
    console.log('🗑️ Botón eliminar producto presionado:', productName);
    
    Alert.alert(
      'Eliminar producto',
      `¿Estás seguro de que quieres remover "${productName}" del carrito?`,
      [
        {
          text: 'Cancelar',
          onPress: () => console.log('❌ Usuario canceló remover producto'),
          style: 'cancel',
        },
        {
          text: 'Eliminar',
          onPress: async () => {
            console.log('✅ Usuario confirmó remover producto');
            try {
              await removeFromCart(productId);
              console.log('✅ Producto removido exitosamente');
            } catch (error) {
              console.error('❌ Error removing item:', error);
              Alert.alert('Error', 'No se pudo eliminar el producto');
            }
          },
          style: 'destructive',
        },
      ],
      { cancelable: true }
    );
  };

  const handleClearCart = async () => {
    console.log('🔘 Botón Vaciar presionado');
    console.log('🛒 Items en carrito:', state.items.length);
    console.log('🛒 Estado completo:', JSON.stringify(state, null, 2));
    
    if (state.items.length === 0) {
      alert('No hay productos en el carrito');
      return;
    }

    if (state.isLoading) {
      console.log('⚠️ Carrito está cargando, esperando...');
      return;
    }

    console.log('📢 Mostrando confirmación de vaciado');
    
    // Usar window.confirm para web (funciona mejor que Alert.alert)
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres eliminar todos los productos del carrito?\n\n` +
      `Se eliminarán ${state.itemsCount} productos (${state.items.length} artículos diferentes).`
    );
    
    if (confirmed) {
      console.log('✅ Usuario confirmó vaciar carrito');
      try {
        console.log('🗑️ Ejecutando clearCart()...');
        await clearCart();
        console.log('✅ clearCart() completado exitosamente');
        alert('✅ El carrito ha sido vaciado correctamente');
      } catch (error) {
        console.error('❌ Error clearing cart:', error);
        alert('❌ No se pudo vaciar el carrito. Intenta nuevamente.');
      }
    } else {
      console.log('❌ Usuario canceló el vaciado del carrito');
    }
  };

  const handleCheckout = async () => {
    if (state.items.length === 0) {
      Alert.alert('Carrito vacío', 'Agrega productos al carrito antes de proceder');
      return;
    }

    if (!user) {
      Alert.alert('Inicia sesión', 'Debes iniciar sesión para realizar el pedido');
      return;
    }

    // Navegar a la pantalla de checkout en lugar de crear el pedido directamente
    (navigation as any).navigate('CartCheckout');
  };

  const renderCartItem = ({ item }: { item: PersistentCartItem }) => (
    <View style={styles.cartItem}>
      <View style={styles.itemImageContainer}>
        {item.imagen ? (
          <Image
            source={{ uri: item.imagen }}
            style={styles.itemImage}
            onError={() => console.log('Error loading image:', item.imagen)}
          />
        ) : (
          <View style={[styles.itemImage, styles.placeholderImage]}>
            <Text style={styles.placeholderText}>📦</Text>
          </View>
        )}
      </View>
      
      <View style={styles.itemDetails}>
        <Text style={styles.itemName}>{item.producto_nombre}</Text>
        <Text style={styles.itemPrice}>Q{parseFloat(String(item.precio_unitario || 0)).toFixed(2)} c/u</Text>
        
        <View style={styles.quantityContainer}>
          <TouchableOpacity
            style={[styles.quantityButton, isUpdating && styles.quantityButtonDisabled]}
            onPress={() => handleQuantityChange(item.id_producto, -1)}
            disabled={isUpdating}
          >
            <Text style={styles.quantityButtonText}>-</Text>
          </TouchableOpacity>
          
          <Text style={styles.quantity}>{item.cantidad}</Text>
          
          <TouchableOpacity
            style={[styles.quantityButton, isUpdating && styles.quantityButtonDisabled]}
            onPress={() => handleQuantityChange(item.id_producto, 1)}
            disabled={isUpdating}
          >
            <Text style={styles.quantityButtonText}>+</Text>
          </TouchableOpacity>
        </View>
      </View>
      
      <View style={styles.itemActions}>
        <Text style={styles.itemTotal}>Q{parseFloat(String(item.subtotal || 0)).toFixed(2)}</Text>
        <TouchableOpacity
          style={styles.removeButton}
          onPress={() => handleRemoveItem(item.id_producto, item.producto_nombre)}
        >
          <Text style={styles.removeButtonText}>🗑️</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  if (state.items.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>🛒 Carrito de Compras</Text>
        </View>
        
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyIcon}>🛒</Text>
          <Text style={styles.emptyTitle}>Carrito vacío</Text>
          <Text style={styles.emptyDescription}>
            No tienes productos en tu carrito.{'\n'}
            Explora nuestros productos y agrégalos aquí.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>🛒 Carrito de Compras</Text>
        <Text style={styles.subtitle}>{state.itemsCount} producto{state.itemsCount !== 1 ? 's' : ''}</Text>
      </View>

      <FlatList
        data={state.items}
        renderItem={renderCartItem}
        keyExtractor={(item) => item.id_producto.toString()}
        style={styles.cartList}
        showsVerticalScrollIndicator={false}
      />

      <View style={styles.footer}>
        <View style={styles.totalContainer}>
          <Text style={styles.totalLabel}>Subtotal:</Text>
          <Text style={styles.totalAmount}>Q{parseFloat(String(state.subtotal || 0)).toFixed(2)}</Text>
        </View>
        
        {/* Mostrar saldo de billetera virtual */}
        <View style={styles.walletContainer}>
          <Text style={styles.walletLabel}>💰 Billetera Virtual:</Text>
          {loadingWallet ? (
            <Text style={styles.walletAmount}>Cargando...</Text>
          ) : (
            <Text style={styles.walletAmount}>Q{walletBalance}</Text>
          )}
        </View>
        
        {/* Nota informativa sobre uso del saldo */}
        {parseFloat(walletBalance) > 0 && (
          <View style={styles.walletInfoContainer}>
            <Text style={styles.walletInfoText}>
              ℹ️ Puedes usar tu saldo como forma de pago en el checkout
            </Text>
          </View>
        )}
        
        <View style={styles.footerButtons}>
          <TouchableOpacity
            style={[styles.clearButton, (state.isLoading || state.items.length === 0) && styles.clearButtonDisabled]}
            onPress={() => {
              console.log('🚨🚨🚨 BOTÓN VACIAR PRESIONADO - INICIO 🚨🚨🚨');
              console.log('Items en carrito:', state.items.length);
              console.log('isLoading:', state.isLoading);
              if (state.isLoading || state.items.length === 0) {
                console.log('⛔ Botón deshabilitado, ignorando click');
                return;
              }
              handleClearCart();
            }}
            activeOpacity={0.7}
          >
            <Text style={styles.clearButtonText}>
              {state.isLoading ? '⏳' : '🗑️'} Vaciar
            </Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.checkoutButton, isProcessingOrder && styles.checkoutButtonDisabled]}
            onPress={handleCheckout}
            disabled={isProcessingOrder}
          >
            <Text style={styles.checkoutButtonText}>
              🛒 Proceder al Pago
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {state.isOnline && (
        <View style={styles.onlineIndicator}>
          <Text style={styles.onlineText}>🟢 Conectado</Text>
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    backgroundColor: '#e74c3c',
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
  },
  subtitle: {
    fontSize: 16,
    color: 'white',
    opacity: 0.9,
    marginTop: 4,
  },
  cartList: {
    flex: 1,
    paddingHorizontal: 15,
    paddingTop: 15,
  },
  cartItem: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 15,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  itemImageContainer: {
    marginRight: 15,
  },
  itemImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
  },
  placeholderImage: {
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderText: {
    fontSize: 24,
  },
  itemDetails: {
    flex: 1,
    marginRight: 10,
  },
  itemName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 4,
  },
  itemPrice: {
    fontSize: 14,
    color: '#7f8c8d',
    marginBottom: 8,
  },
  quantityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  quantityButton: {
    backgroundColor: '#e74c3c',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  quantityButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.5,
  },
  quantityButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
  quantity: {
    marginHorizontal: 15,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  itemActions: {
    alignItems: 'flex-end',
  },
  itemTotal: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#27ae60',
    marginBottom: 8,
  },
  removeButton: {
    padding: 8,
  },
  removeButtonText: {
    fontSize: 18,
  },
  footer: {
    backgroundColor: 'white',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e1e8ed',
  },
  totalContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e8ed',
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  totalAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#e74c3c',
  },
  walletContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: '#ecf9f2',
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#27ae60',
  },
  walletLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#27ae60',
  },
  walletAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#27ae60',
  },
  walletInfoContainer: {
    backgroundColor: '#e3f2fd',
    padding: 10,
    borderRadius: 6,
    marginBottom: 15,
  },
  walletInfoText: {
    fontSize: 13,
    color: '#1976d2',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  footerButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  clearButton: {
    flex: 1,
    backgroundColor: '#95a5a6',
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginRight: 6,
  },
  clearButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.5,
  },
  clearButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  checkoutButton: {
    flex: 2,
    backgroundColor: '#27ae60',
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginLeft: 6,
  },
  checkoutButtonDisabled: {
    backgroundColor: '#bdc3c7',
  },
  checkoutButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: 16,
    color: '#7f8c8d',
    textAlign: 'center',
    lineHeight: 24,
  },
  onlineIndicator: {
    position: 'absolute',
    top: 80,
    right: 15,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  onlineText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
});

export default CartScreenSimple;
